import { useResourcesState } from "@/state/resourcesState";
import css from "./OrderDetails.module.css";
import React, { useMemo } from "react";
import { useFurnitureManagementState } from "@/state/furnitureManagementState";
import _ from "lodash";
import { Column } from "@/dom/components/Panel/Column";
import { Row } from "@/dom/components/Panel/Row";
import { Typography } from "@/dom/components/Typography/Typography";
import { Button } from "@/dom/components/Button/Button";
import { Panel } from "@/dom/components/Panel/Panel";
import { formatPrice } from "@/utils/string";
import { formatDimensions } from "@/utils/dimensions";
import { Icon } from "@/dom/components/Icon/Icon";
import { downloadInvoice } from "@/api/invoice";

type Props = {
    onClose: () => void;
};

export const OrderDetails: React.FC<Props> = ({ onClose }) => {
    const furnitureInstances = useFurnitureManagementState((state) => state.furnitureInstances);
    const allFurniture = useResourcesState((state) => state.furniture);

    const totalPrice = useMemo(() => {
        return Object.values(furnitureInstances).reduce((acc, instance) => {
            const furniture = allFurniture[instance.furnitureId];
            return acc + furniture.data.price;
        }, 0);
    }, [furnitureInstances, allFurniture]);

    const items = useMemo(() => {
        const furnitureInstancesArray = Object.values(furnitureInstances);
        const furnitureIds = _.uniq(
            furnitureInstancesArray.map((instance) => instance.furnitureId)
        );

        return furnitureIds.map((furnitureId) => {
            const furniture = allFurniture[furnitureId];
            const instances = furnitureInstancesArray.filter(
                (instance) => instance.furnitureId === furnitureId
            );

            return {
                furniture,
                count: instances.length,
                totalPrice: instances.length * furniture.data.price,
            };
        });
    }, [furnitureInstances, allFurniture]);

    const handlePrint = async () => {
        const data = items.reduce((acc, item) => {
            acc[item.furniture.data.code] = item.count;
            return acc;
        }, {} as Record<string, number>);

        await downloadInvoice(data);

        onClose();
    };

    const handleEdit = () => {
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <div className={css.main}>
            <div className={css.content}>
                <Column overflow="hidden">
                    <Row className={css.tableHeader}>
                        <Typography variant="subtitle">Детали заказа</Typography>
                        <Row>
                            <Button variant="text" size="small" onClick={handlePrint}>
                                <Icon icon="printer" />
                                Печать
                            </Button>
                            <Button variant="text" size="small" onClick={handleEdit}>
                                <Icon icon="pencil" />
                                Редактировать
                            </Button>
                        </Row>
                    </Row>
                    <div className={css.table}>
                        {items.map((item, index) => (
                            <React.Fragment key={index}>
                                <Row justify="flex-start" align="flex-start">
                                    <div className={css.imageContainer}>
                                        <img
                                            src={item.furniture.resources.previewPath}
                                            alt={item.furniture.data.name}
                                        />
                                    </div>
                                    <Column>
                                        <Typography variant="subtitle">
                                            {item.furniture.data.name}
                                        </Typography>
                                        <Typography variant="body-medium">
                                            {formatDimensions(item.furniture.data.dimensions)}
                                        </Typography>
                                    </Column>
                                </Row>
                                <Typography variant="subtitle">
                                    {formatPrice(item.furniture.data.price)}
                                </Typography>
                                <Typography variant="subtitle">{item.count}шт</Typography>
                                <Row justify="flex-end" align="flex-start">
                                    <Typography variant="subtitle">
                                        {formatPrice(item.totalPrice)}
                                    </Typography>
                                </Row>
                            </React.Fragment>
                        ))}
                    </div>
                </Column>
                <Column>
                    <Panel className={css.summary}>
                        <Row>
                            <Typography variant="title">{formatPrice(totalPrice)}</Typography>
                            <Button variant="secondary" size="small" onClick={handleClose}>
                                Завершить
                            </Button>
                        </Row>
                    </Panel>
                </Column>
            </div>
        </div>
    );
};
